import { BooleanOperation } from '~/engine/util/algorithms/sdf/SDFOperations';
import { StepPayload, StructureReference } from './types';

/** One element of a step chain written out as a formula. */
export type FormulaToken<T> =
    | { kind: 'operand'; operand: T }
    | { kind: 'operator'; operation: BooleanOperation }
    | { kind: 'open' }
    | { kind: 'close' };

/**
 * Writes a step chain out in the order it runs, each step folding its right
 * operands in left to right. A change of operator brackets everything before
 * it, so `(A OR B) SUBTRACT C` never reads as `A OR (B SUBTRACT C)`. A step
 * with no right operands is skipped. If the first step that is kept starts
 * from the previous result, there are no tokens.
 */
export function formulaTokens<T>(
    steps: readonly StepPayload<T>[]
): FormulaToken<T>[] {
    let tokens: FormulaToken<T>[] = [];
    let previous: BooleanOperation | null = null;
    for (const step of steps) {
        if (step.rightOperands.length === 0) {
            continue;
        }
        if (tokens.length === 0) {
            if (step.leftOperand === 'previous-result') {
                return [];
            }
            tokens = [{ kind: 'operand', operand: step.leftOperand }];
        } else if (step.operation !== previous) {
            tokens = [{ kind: 'open' }, ...tokens, { kind: 'close' }];
        }
        for (const operand of step.rightOperands) {
            tokens.push({ kind: 'operator', operation: step.operation });
            tokens.push({ kind: 'operand', operand });
        }
        previous = step.operation;
    }
    return tokens;
}

/** The word that stands for each operation in formula text. */
export const FORMULA_OPERATOR_TEXT: Record<BooleanOperation, string> = {
    [BooleanOperation.OR]: 'OR',
    [BooleanOperation.AND]: 'AND',
    [BooleanOperation.AND_NOT]: 'SUBTRACT',
    [BooleanOperation.XOR]: 'XOR',
};

/**
 * Formula tokens as text, each operand quoted by the name `nameOf` gives
 * it, for example `("A" OR "B") SUBTRACT "C"`.
 */
export function formulaText<T>(
    tokens: readonly FormulaToken<T>[],
    nameOf: (operand: T) => string
): string {
    let text = '';
    let previous: FormulaToken<T>['kind'] | null = null;
    for (const token of tokens) {
        const glued =
            previous === null || previous === 'open' || token.kind === 'close';
        text += glued ? '' : ' ';
        switch (token.kind) {
            case 'operand':
                text += `"${nameOf(token.operand)}"`;
                break;
            case 'operator':
                text += FORMULA_OPERATOR_TEXT[token.operation];
                break;
            case 'open':
                text += '(';
                break;
            case 'close':
                text += ')';
                break;
        }
        previous = token.kind;
    }
    return text;
}

/** The formula text of a step chain, named by its ROIs. */
export function buildFormulaFromSteps(
    steps: readonly StepPayload<StructureReference>[]
): string {
    return formulaText(formulaTokens(steps), (ref) => ref.roi.name);
}
